/* eslint-disable max-len */
import { Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { HotToastService } from '@ngneat/hot-toast';
import { ZuperDialogService } from '@zuper/components/zuper-dialog';
import { DataCollectorService } from 'app/core/services/data_collector_services/data_collector_service.service';
import { UtilityService } from 'app/core/services/utility_functions/utility_functions.service';
import _ from 'lodash';
import dayjs, { Dayjs } from 'dayjs';
import moment, { Moment } from 'moment';
import { DateFormatPipe } from 'app/core/pipes/dateFormat.pipe';
import { TimeFormatPipe } from 'app/core/pipes/timeFormat.pipe';
import { TitleCasePipe } from '@angular/common';
import { NewTimeOffComponent } from 'app/components/new-timeoff/new-timeoff.component';
@Injectable({
  providedIn: 'root'
})
export class CalendarService {
  public get baseUrl(): string {
    return `${this._dataCollectorService.baseurl()}`;
  };
  public get metaDataUrl(): string {
    return `${this.baseUrl}/meta_data`;
  };
  public get eventsCountUrl(): any{
    return `${this.baseUrl}/schedule/calendar/count`;
  }
  public get employeeUrl(): any{
    return `${this.baseUrl}/user/all`;
  }
  public get teamUrl(): any{
    return `${this.baseUrl}/teams/summary`;
  }
  public get timeofflist(): any{
    return `${this._dataCollectorService.baseurl()}/timesheets/request/timeoff`;
  }
  public get jobDetailsUrl(): any{
    return `${this.baseUrl}/jobs`;
  }
  public get jobScheduleUrl(): any{
    return `${this.baseUrl}/jobs/schedule`;
  }
  public get filterAPIurls(): any{
    return {
      jobCategoryUrl: `${this.baseUrl}/jobs/category?populate_statuses=true`,
      skillsUrl: `${this.baseUrl}/skills`,
      serviceTerritoryUrl: `${this.baseUrl}/territory`,
      routesUrl: `${this.baseUrl}/routes`,
      saveFilterUrl: `${this.baseUrl}/misc/quick_filters`,
      customerUrl: `${this.baseUrl}/customers`,
      organizationUrl: `${this.baseUrl}/organization`,
      propertyUrl: `${this.baseUrl}/property`,
      teamUrl: `${this.baseUrl}/teams/summary`,
      employeeUrl: `${this.baseUrl}/user/all`,
      tagUrl:`${this.baseUrl}/company_tags`,
      projectUrl:`${this.baseUrl}/projects`
    };
  }
  get timeoffDetailsUrl(): any{
    return `${this.baseUrl}/timesheets/request/timeoff`;
  };
  timeFormat: any;
  dateFormat: any;
  browserTimezone: any;
  dateTimeFormat: any;
  defaultJobAttributes: any;
  selectedTimezone = { 'JOBS': null};
  activeDispatchTab: 'JOBS' | 'ROUTES' = 'JOBS';
  moduleAccess: ModuleAccess = {project: this._utilityService.hasModuleAccess('PROJECT')  };
  constructor(private _dataCollectorService: DataCollectorService,
    private _utilityService: UtilityService,
    private _toastService: HotToastService,
    private _zuperDialogService: ZuperDialogService,
    private dateFormatPipe: DateFormatPipe,
    private timeFormatPipe: TimeFormatPipe,
    private titlecasePipe: TitleCasePipe) {
      this.timeFormat = this._utilityService.timeFormat;
      this.dateFormat = this._utilityService.dateFormat;
      this.dateTimeFormat = this._utilityService.dateTimeFormat;
      this.browserTimezone = moment.tz.guess();
      this.selectedTimezone = { 'JOBS': this.browserTimezone };
     }

  jobDetailsChangeConfirmationDialog(jobDetails: any, type: 'unassign' | 'delete' | 'clearSchedule', optionalData?: any): MatDialogRef<any> {
    const prefix = _.get(jobDetails, 'prefix') || '';
    const workOrderNumber = _.get(jobDetails, 'work_order_number') || '';
    const jobTitle = _.get(jobDetails, 'job_title') || '';
    const jobIdTitle = `${workOrderNumber !== '' ? '#' : ''}${prefix} ${workOrderNumber} ${jobTitle}`;
    let dialogTitle: string; let dialogMessage: string;
    let confirmLabel: string; let confirmDisplayKey: string;
    let color: string = 'primary';

    if (type === 'delete') {
      dialogTitle = this._utilityService.getLocaleValue('text.delete_job', 'Delete Job');
      dialogMessage = `${this._utilityService.getLocaleValue('delete.job.confirmation.message')} <span class="text-gray-900 font-medium">
      '${jobIdTitle}'</span> ?`;
      confirmLabel = 'Delete';
      confirmDisplayKey = 'delete.text';
      color = 'error';
    }
    else if (type === 'unassign') {
      const userName = optionalData?.userName || '';
      dialogTitle = this._utilityService.getLocaleValue('text.unassign_job', 'Unassign Job');
      dialogMessage = `${this._utilityService.getLocaleValue('dispatchBoard.scheduler.dialog.unassign.message')} <span class="text-gray-900 font-medium">
      ${userName} </span> from <span class="text-gray-900 font-medium">'${jobIdTitle}'</span> ?`;
      confirmLabel = 'Unassign';
      confirmDisplayKey = 'text.unassign';
    }
    else if (type === 'clearSchedule') {
      dialogTitle = this._utilityService.getLocaleValue('text.clear_schedule', 'Clear Schedule');
      dialogMessage = this.getMessageWithNote(jobDetails);
      confirmLabel = 'Update';
      confirmDisplayKey = 'text.clear_schedule';
    }

    const confirmationDialog = this._zuperDialogService.open({
      title: dialogTitle,
      type: 'error',
      message: dialogMessage,
      actions: {
        confirm: {
          show: true,
          label: confirmLabel,
          color: color,
          confirmDisplayKey: confirmDisplayKey,
        },
        cancel: {
          show: true,
          label: 'Cancel',
          cancelDisplayKey: 'dispatchBoard.scheduler.dialog.delete.cancel'
        }
      },
      dismissible: false,
      extraClass: 'job-delete-dialog'
    });
    return confirmationDialog;
  }

  deleteJob(jobUid: any, statusCallback: (status: boolean) => void): void {
    const loader = this._toastService.loading(this._utilityService.getLocaleValue('calendar.job.delete'));
    this._dataCollectorService.deleteData(this.getJobDeleteUrl(jobUid))
      .subscribe((response: any) => {
        loader.close();
        this._toastService.success(response.message);
        statusCallback(true);
      }, (error: any) => {
        loader.close();
        statusCallback(false);
      });
  }

  getJobDeleteUrl(jobUid: string): string {
    return `${this._dataCollectorService.baseurl()}/jobs/${jobUid}/delete`;
  }

  getStartEndTime(startDateTime: any, endDateTime: any): any[] {
    let startDate: any;
    let endDate: any;
    if (!startDateTime || !endDateTime) { return [null, null]; }
    startDateTime = dayjs(startDateTime);
    endDateTime = dayjs(endDateTime);
    const customFormat = this._utilityService.dateTimeFormat;
    if (startDateTime.isSame(endDateTime, 'date')) {
      startDate = dayjs(startDateTime).format(customFormat);
      endDate = endDateTime.format(this._utilityService.timeFormat);
    }
    else {
      startDate = dayjs(startDateTime).format(customFormat);
      endDate = dayjs(endDateTime).format(customFormat);
    }
    return [startDate, endDate];
  }

  editTimeOffEvent(timeoffDetails: any, userDetails: any, teamDetails: any, selectedTimezone: any, timeoffEditSuccess: (status: boolean, userUid: string, timeoff: any) => void): void {
       const user = {
      first_name: userDetails[0]?.first_name, last_name: userDetails[0]?.last_name, user_uid: userDetails[0]?.user_uid, email: userDetails[0]?.email
    };
    const team = {
      team_name: teamDetails[0]?.team_name, team_uid:  teamDetails[0]?.team_uid
    };
    // startDate = dayjs(startDateTime).format(customFormat);
    // endDate = endDateTime.format(this._utilityService.timeFormat);
    const startDateTime = moment(timeoffDetails?.request_from).tz(this.selectedTimezone['JOBS']);
    const endDateTime = moment(timeoffDetails?.request_to).tz(this.selectedTimezone['JOBS']);
    const timeoffData = { startDate: startDateTime, startTime: startDateTime.format('HH:mm:ss'), endDate: endDateTime, endTime: endDateTime.format('HH:mm:ss'), user: user, team: team, timezone: selectedTimezone, reason: timeoffDetails.request_reason, reasonUid: timeoffDetails?.timeoff_request_type?.timeoff_request_type_uid || timeoffDetails.request_reason, remarks: timeoffDetails.request_remarks, request_uid: timeoffDetails.request_uid };
    const editTimeoffDialogInstance = this._zuperDialogService.openCustom(NewTimeOffComponent,
      { disableClose: true, extraClass: 'new-timeoff', timeoffData: timeoffData, action: 'edit' }
    );

    editTimeoffDialogInstance.afterClosed()
    .subscribe((result: any) => {
      if (result.status) {
        const requestUid = result.request_uid;
        const timeoffRequestUrl = `${this.timeoffDetailsUrl}/${requestUid}`;
        this._dataCollectorService.getData(timeoffRequestUrl)
          .subscribe((response) => {
            const timeoff = response?.data;
            const requestedBy = timeoff.requested_by;
            const _userUid = requestedBy.user_uid;
            timeoffEditSuccess(true, _userUid, timeoff);
          });
      }
    });
    /* eslint-enable @typescript-eslint/naming-convention */
  }

  sanitizeTemplateMetaData(metaData: any): any {
    if (metaData) {
      if (metaData?.grid?.card_view) {
        metaData.grid.card_view = this.addDisplayKeySelectedAttributes(metaData.grid.card_view, this.defaultJobAttributes);
      }
      if (metaData?.grid?.month_card_view) {
        metaData.grid.month_card_view = this.addDisplayKeySelectedAttributes(metaData.grid.month_card_view, this.defaultJobAttributes);
      }
    }
    return metaData;
  }
  
  getDisplayKey(attribute: any, attributeList: any[]): string {
    if (attribute?.label && attribute?.selector && attributeList?.length) {
      if (attribute?.displayKey) {
        return attribute?.displayKey;
      }
      const baseAttribute = _.find(attributeList, { label: attribute.label, selector: attribute.selector });
      if (baseAttribute && baseAttribute?.displayKey) {
        return baseAttribute?.displayKey;
      }
      return null;
    }
  }
  
  addDisplayKeySelectedAttributes(selectedAttributes: any, allAttributes: any): any {
    if (Array.isArray(selectedAttributes)) {
      selectedAttributes = selectedAttributes.map((attribute: any) => this.setDisplayKeyAndValue(attribute, allAttributes));
      return selectedAttributes;
    } else if (selectedAttributes && typeof selectedAttributes === 'object') {
      const filteredList = {};
      let newRowIndex = 1;
      Object.keys(selectedAttributes).map(key => Number(key)).forEach((rowNumber) => {
        filteredList[newRowIndex] = [];
        selectedAttributes[rowNumber].forEach((attribute: any, columnIndex: number) => {
          if (_.some(allAttributes, { key: attribute.key })) {
            attribute.column = columnIndex + 1;
            attribute.row = newRowIndex;
            attribute = this.setDisplayKeyAndValue(attribute, allAttributes);
            filteredList[newRowIndex].push(attribute);
          }
        });
        if (filteredList[newRowIndex].length === 0) {
          delete filteredList[newRowIndex];
        } else {
          newRowIndex++;
        }
      });
      return filteredList;
    }
  }
  
  setDisplayKeyAndValue(attribute: any, allAttributes: any): void {
    if (!attribute || !allAttributes) return attribute;
    attribute['displayKey'] = this.getDisplayKey(attribute, allAttributes);
    attribute['displayKeyValue'] = this._utilityService.getLocaleValue(attribute.displayKey) || attribute?.label;
    if (!attribute['displayKeyValue']) {
      attribute['displayKeyValue'] = attribute?.label;
    }
    return attribute;
  }
  

  getGridCardRenderer(jobDetails: any): string {
    let templateHtml: string = '';
    if (jobDetails) {
      const jobTitle = this.getTitle(jobDetails?.prefix, jobDetails?.work_order_number, jobDetails?.job_title);
      const dueDate = jobDetails?.due_date;
      let duration: any;
      let isOverDue: boolean = false;
      if (dueDate) {
        duration = moment(dueDate).diff(moment(), 'd');
        if (duration < 0) {
          isOverDue = true;
        }
      }
      duration = Math.abs(duration);
      templateHtml += `
      <div class="w-full h-full min-w-72 font-normal text-base primary-font text-gray-700 m-auto p-2 bg-white border border-gray-200 cursor-pointer rounded-lg shadow dark:bg-gray-800 dark:border-gray-700">
        <div class="w-full h-full">
          <div class="w-1/12 h-full float-left mt-1"><i class="ti ti-grip-horizontal text-2xl"></i></div>
          <div class="w-10/12 card-content float-left mt-1">
            <div title="${jobTitle}" class="w-full block truncate text-base primary-font font-normal text-gray-700">
              <span>${jobTitle}</span>
            </div>
          <div class="w-full h-full flex items-center pt-4 divide-x-axis gap-2">
            <span class='flex items-center text-base primary-font font-normal text-gray-700' title="${this._utilityService.getLocaleValue('due.date.text', 'Due Date')}"><i class="ti ti-clock text-xl mr-1"></i>${dueDate ? this.dateFormatPipe.transform(dueDate) : '--'}</span>
            <span class="${isOverDue ? 'text-red-600' : 'text-gray-700'} flex items-center pl-2 text-base primary-font font-normal" title="${isOverDue ? this._utilityService.getLocaleValue('text.overdue', 'Overdue') : this._utilityService.getLocaleValue('route.label.duration', 'Duration')}"><i class="ti ti-calendar text-xl mr-1"></i>${isOverDue ? this._utilityService.getLocaleValue('text.overdue', 'Overdue') : `${duration ? duration : this._utilityService.getLocaleValue('text.unshceduled.due_today', 'Due Today')} ${duration ? duration > 1 ? this._utilityService.getLocaleValue('text.days', 'Days').toLowerCase() : this._utilityService.getLocaleValue('text.day', 'Day').toLowerCase(): ''}`}</span>
          </div>
        </div>
      </div>
      </div>`;
    }
    return templateHtml;
  }

  formatJobDetails(customSelectedAttributes: any, jobDetails: any, additionalParams: any = {}): any {
    if (!customSelectedAttributes || !jobDetails) { return; }
    const customData = {};
    if (Array.isArray(customSelectedAttributes)) {
      customSelectedAttributes = { 1: [...customSelectedAttributes] };
    }
    customData['id'] = jobDetails?.job_uid || jobDetails?.route_uid;
    customData['is_recurrence'] = jobDetails?.is_recurrence;
    customData['routeDetails'] = { isRouteJob: jobDetails?.route?.route_uid ? true : false, routeName: jobDetails?.route?.route_name || '' };
    customData['delayed_job'] = jobDetails?.delayed_job;
    customData['detailsType'] = jobDetails?.detailsType || 'job';
    Object.keys(customSelectedAttributes).map(key => Number(key)).forEach((rowNumber: any) => {
      customSelectedAttributes[rowNumber].forEach((attribute: any) => {
        if (attribute.isDefaultField) {
          if (attribute.group_uid === 'current_work_order_status') {
            const statusColor = _.get(jobDetails, attribute.status_color_selector);
            customData[attribute.key] = _.get(jobDetails, attribute.selector);
            customData['status_color_selector'] = statusColor ? statusColor : '#02B875';
          }
          else if (attribute.group_uid === 'work_order_actual_date' || attribute.group_uid === 'work_order_scheduled_date') {
            const selectors = attribute.selector;
            const startDateTime = _.get(jobDetails, selectors[0]);
            const endDateTime = _.get(jobDetails, selectors[1]);
            let formatedStartDateTime: any;
            let formatedEndDateTime: any;
            if (startDateTime && !endDateTime) {
              formatedStartDateTime = dayjs(startDateTime).utc().tz(this.selectedTimezone[this.activeDispatchTab]).format(this.dateTimeFormat);;
            }
            else if (startDateTime && endDateTime) {
              let showOnlyScheduledTime: boolean = false;
              let showScheduledEndDate: boolean = false;
             if(additionalParams?.showOnlyScheduledTime){
              if((additionalParams?.currentView === 'WEEK' || additionalParams?.currentView === 'MONTH')  && additionalParams?.schedulerZoomLevel === 0) {
                showOnlyScheduledTime = true;
                showScheduledEndDate = false;
              }
              else {
                showOnlyScheduledTime = true;
                showScheduledEndDate = true;
              }
             }
              [formatedStartDateTime, formatedEndDateTime] = this.getStartEndTime(startDateTime, endDateTime);
            }
            if (startDateTime) {
              customData[attribute.key] = {};
              customData[attribute.key]['startDateTime'] = formatedStartDateTime;
              if (endDateTime) {
                customData[attribute.key]['endDateTime'] = formatedEndDateTime;
              }
            }
          }
          else if (attribute.group_uid === 'work_order_created_date' || attribute.group_uid === 'created_date') {
            const dateTime = _.get(jobDetails, attribute.selector);
            if (dateTime) {
              customData[attribute.key] = dayjs.utc(dateTime).tz(this.selectedTimezone[this.activeDispatchTab]).format(this.dateTimeFormat);
            }
          }
          else if (attribute.group_uid === 'work_order_due_date') {
            const dateTime = _.get(jobDetails, attribute.selector);
            if (dateTime) {
              customData[attribute.key] = dayjs.utc(dateTime).tz(this.selectedTimezone[this.activeDispatchTab]).format(this.dateFormat);
            }
          }
          else if (attribute.group_uid === 'job_project') {
            const projectDetails = _.get(jobDetails, attribute.selector);
            if (projectDetails) {
              customData[attribute.key] = this.getTitle(projectDetails?.project_prefix, projectDetails?.project_number, projectDetails?.project_name);
            }
          }
          else if (attribute.group_uid === 'work_order_customer_name') {
            const selectors = attribute.selector;
            const firstName = _.get(jobDetails, selectors[0]);
            const lastName = _.get(jobDetails, selectors[1]);
            customData[attribute.key] = this.concatTwoStrings(firstName, lastName);
          }
          else if (attribute.group_uid === 'service_address_city_state') {
            const selectors = attribute.selector;
            const city = _.get(jobDetails, selectors[0]);
            const state = _.get(jobDetails, selectors[1]);
            customData[attribute.key] = this.concatTwoStrings(city, state, '-');
          }
          else if (attribute.group_uid === 'work_order_skillsets') {
            const skills: string[] = _.get(jobDetails, attribute.selector) || [];
            if (skills.length > 0) {
              customData[attribute.key] = {};
              customData[attribute.key]['skill_name'] = skills[0]['skill_name'];
              customData[attribute.key]['skill_count'] = skills.length - 1;
            }
          }
          else if (attribute.group_uid === 'work_order_assigned_to') {
            const assignedTo = _.get(jobDetails, attribute.selector) || [];
            const assignedToTeam = _.get(jobDetails, 'assigned_to_team') || [];
            let profilePic = '';
            let totalAssignees = 0;
            customData[attribute.key] = {};
            const assignedArr = [];
            if (assignedTo.length > 0) {
              assignedTo.forEach((user) => {
                const userObj = {};
                const firstName = _.get(user, ['user', 'first_name']);
                const lastName = _.get(user, ['user', 'last_name']);
                userObj['name'] = this.concatTwoStrings(firstName, lastName);
                profilePic = _.get(user, ['user', 'profile_picture']) || '';
                profilePic = profilePic.includes('profile_picture.jpg') ? '' : profilePic;
                userObj['profile_picture'] = profilePic;
                assignedArr.push(userObj);
              });
              customData[attribute.key]['assignedTo'] = assignedArr;
              totalAssignees = assignedTo.length;
            } else if (assignedTo.length === 0 && assignedToTeam.length > 0) {
              assignedTo.forEach((team) => {
                const teamObj = {};
                teamObj['name'] = _.get(team, ['team', 'team_name']);
                teamObj['profile_picture'] = '';
                assignedArr.push(teamObj);
              });
              customData[attribute.key]['assignedTo'] = assignedArr;
              totalAssignees = assignedToTeam?.length;
            }
            customData[attribute.key]['count'] = totalAssignees - 1;
          }
          else if (attribute.group_uid === 'work_order_number') {
            const prefix = _.get(jobDetails, 'prefix') || '';
            const workOrderNumber = _.get(jobDetails, attribute.selector) || '';
            customData[attribute.key] = `${workOrderNumber !== '' ? '#' : ''}${prefix} ${workOrderNumber}`;
          }
          else {
            customData[attribute.key] = _.get(jobDetails, attribute.selector) ?? '';
          }
        }
        else {

          //Custom field
          // eslint-disable-next-line @typescript-eslint/naming-convention
          jobDetails.custom_fields = jobDetails?.custom_fields.map(job => ({ ...job, group_uid: !job['group_uid'] ? '' : job['group_uid'] }));
          let searchGroupUid = attribute.group_uid;
          if (attribute.key.includes('default_uid')) {
            searchGroupUid = '';
          }

          // eslint-disable-next-line @typescript-eslint/naming-convention
          const customValue = _.find(jobDetails.custom_fields, { label: attribute.originalLabel, group_uid: searchGroupUid });
          if (customValue) {
            if (customValue.type === 'SINGLE_LINE') {
              if (this._utilityService.isURL(customValue.value)) {
                customData[attribute.key] = {};
                customData[attribute.key]['href'] = customValue.value;
              }
              else {
                customData[attribute.key] = customValue.value;
              }
            }
            else if (customValue.type === 'DATE' && !this._utilityService.isEmptyOrUndefined(customValue.value)) {
              customData[attribute.key] = this.dateFormatPipe.transform(customValue.value);
            }
            else if (customValue.type === 'TIME' && !this._utilityService.isEmptyOrUndefined(customValue.value)) {
              customData[attribute.key] = this.timeFormatPipe.transform(customValue.value, false, 'HH:mm:ss');
            }
            else if (customValue.type === 'DATETIME' && !this._utilityService.isEmptyOrUndefined(customValue.value)) {
              customData[attribute.key] = dayjs.utc(customValue.value, 'YYYY-MM-DD HH:mm:ss').tz(this.selectedTimezone[this.activeDispatchTab]).format(this.dateTimeFormat);
            }
            else if (customValue.type === 'FILE' && !this._utilityService.isEmptyOrUndefined(customValue.value)) {
              customData[attribute.key] = {};
              customData[attribute.key]['href'] = customValue.value;
            }
            else if (customValue.type === 'LOOKUP' && !this._utilityService.isEmptyOrUndefined(customValue.value)) {
              customData[attribute.key] = {};
              customData[attribute.key]['value'] = customValue.value;
              customData[attribute.key]['href'] = this._utilityService.getLink(customValue.module_name, customValue.ref_uid);
            }
            else {
              customData[attribute.key] = customValue?.value ? customValue?.value : '';
            }
          }
        }
      });
    });
    return customData;
  }

  getTitle(prefix: string, eventNumber: string, eventTitle: string): string {
    return `#${prefix || ''}${eventNumber || ''} ${eventTitle || ''}`;
  }

  getMessageWithNote(jobDetails: any): string {
    const baseMessage = this._utilityService.getLocaleValue('dispatchBoard.scheduler.dialog.clear_schedule.message');
    if (jobDetails?.route) {
      return `${baseMessage} ?<br><p class="font-medium text-md mt-2 text-red-600">${this._utilityService.getLocaleValue('dispatchBoard.scheduler.dialog.clear_schedule.note')} ' ${jobDetails?.route?.route_name} '</p>`;
    }
    return `${baseMessage} ?`;
  }

  clearScheduleJobFromRoute(jobDetails: any, statusCallback: (status: boolean) => void): void {
      // Check if job details contain route_uid
      if (jobDetails?.route?.route_uid) {
        const removeJobParam = {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          jobs: [{ job_uid: jobDetails?.job_uid }], unassign_users: false
        };
        const routeUid = jobDetails?.route?.route_uid;

        this._dataCollectorService.deleteDataPayload(this.getUpdateRouteJobUrl(routeUid), removeJobParam)
        .subscribe((response: any) => {
          this._toastService.success(response.message);
          statusCallback(true);
        }, (error: any) => {
          statusCallback(false);
        });
      }
  }

  clearScheduleJob(jobDetails: any, statusCallback: (status: boolean) => void): void {
      // Check if job details contain route_uid
      if (jobDetails?.route?.route_uid) {
        const removeJobParam = {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          jobs: [{ job_uid: jobDetails?.job_uid }], unassign_users: false
        };
        const routeUid = jobDetails?.route?.route_uid;

        this._dataCollectorService.deleteDataPayload(this.getUpdateRouteJobUrl(routeUid), removeJobParam)
        .subscribe((response: any) => {
          this.clearScheduleJobUpdate(jobDetails, statusCallback);
        }, (error: any) => {});
      }
      else {
        this.clearScheduleJobUpdate(jobDetails, statusCallback);
      }
  }

  clearScheduleJobUpdate(jobDetails: any, statusCallback: (status: boolean) => void): void {
    // Payload for updating the job details
    const payload: any = {
        job: {
            job_uid: jobDetails.job_uid,
            due_date: moment(jobDetails?.due_date)?.endOf('day')?.utc()?.format('YYYY-MM-DD HH:mm:ss'),
        }
    };
    if(jobDetails?.job_timezone && this._utilityService.isTimeZoneEnabled === false){
      payload.job['job_timezone'] = null;
    }
    // Make the API call to clear the job schedule
    this._dataCollectorService.putData(`${this.jobDetailsUrl}?clear_schedule=true`, payload).subscribe(
        (response: any) => {
            this._toastService.success(response.message);
            statusCallback(true);
        },
        (error: any) => {
            statusCallback(false);
        }
    );
  }

  getUpdateRouteJobUrl(routeUid: string): string {
    return `${this._dataCollectorService.baseurl()}/routes/${routeUid}/job`;
  }

  concatTwoStrings(value1: string, value2: string, delimiter: string = ' '): string {
    let result = '';
    if (value1 && value2) {
      result = value1 + delimiter + value2;
    }
    else if (value1) {
      result = value1;
    }
    else if (value2) {
      result = value2;
    }
    return result;
  }

  convertSecondsToHours(seconds: number, unitShortForm: boolean = false): string {
    const totalMinutes = seconds / 60;
    const localeHour = unitShortForm ? 'hr': this._utilityService.getLocaleValue('label.hour', 'hour');
    const localeHours = unitShortForm ? 'hrs': this._utilityService.getLocaleValue('label.hours', 'hours');
    const localeMinute = unitShortForm ? 'min': this._utilityService.getLocaleValue('label.minute', 'minute');
    const localeMinutes = unitShortForm ? 'mins': this._utilityService.getLocaleValue('label.minutes', 'minutes');
    if (!totalMinutes) { return `${0} ${localeMinute}`; }
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const h = hours > 0 ? `${hours?.toFixed()} ${Number(hours?.toFixed()) <= 1 ? localeHour : localeHours}` : '';
    const m = minutes > 0 ? ` ${minutes?.toFixed()} ${Number(minutes?.toFixed()) <= 1 ? localeMinute : localeMinutes}` : '';
    return `${h} ${m}`;
  }
}

export interface ModuleAccess {
  project: boolean;
}
